(function () {
  'use strict';

  const RECOMMENDATIONS_SECTION_ID = 'cart-drawer-recommendations';
  const CART_UPSELL_SLIDER_SELECTOR = '[data-cart-upsell]';
  const TRACK_SELECTOR = '[data-slider-track]';

  /**
   * Search & Discovery intents, in display order. Array order is the display order: Promise.all
   * preserves it regardless of which response lands first, so every `related` product outranks
   * every `complementary` one and complementary only fills seats related left empty.
   */
  const RECOMMENDATION_INTENTS = ['related', 'complementary'];
  /** The Product Recommendations API caps limit at 10 per intent. */
  const RECOMMENDATIONS_MAX_LIMIT = 10;
  const UPSELL_MODAL_FLAG = 'data-cart-upsell-modal';

  const DEBOUNCE_MS = 400;
  const SCROLL_DEBOUNCE_MS = 50;
  const CACHE_MAX_AGE_MS = 60000;

  const LAYOUT_SINGLE = 'is-single';
  const LAYOUT_DOUBLE = 'is-double';
  const LAYOUT_MULTIPLE = 'is-multiple';
  const LAYOUT_CAROUSEL = 'is-carousel';
  const ARROW_HIDDEN_CLASS = 'is-hidden';

  const CART_PAGE_UPSELL_CONTEXT = 'cart-page';

  const CAROUSEL_BREAKPOINT_TABLET = 750;
  const CAROUSEL_BREAKPOINT_DESKTOP = 990;
  const CAROUSEL_MAX_COL_DESKTOP = 4;
  const CAROUSEL_MAX_COL_TABLET = 2;
  const CAROUSEL_MAX_COL_MOBILE = 1;
  const CAROUSEL_GAP_PX = 16;
  const UPSELL_DETAILS_SELECTOR = '.cart-upsell-details';
  const UPSELL_WRAPPER_SELECTOR = '.js-cart-upsell-wrapper';
  const OVERLAY_VISIBLE_CLASS = 'is-visible';
  const VIEWPORT_TRANSITION_CLASS = 'is-transitioning';
  const TRANSITION_DURATION_MS = 250;

  let recommendationsCache = Object.create(null);
  const inFlightRecommendations = new Set();
  let cartUpdateUnsubscriber;
  let addFromUpsellSlider = false;
  const initialTrackHtmlBySlider = new WeakMap();

  let lastKnownCartData = null;

  /** Last applied dynamic recommendations HTML; restored on cart update to avoid manual-upsell flash. */
  let lastDynamicRecommendationsHtml = '';

  const REFRESH_DATA_ATTR = 'upsellRefreshing';

  /* Helpers */
  const qs = (el, sel) => el.querySelector(sel);
  const qsa = (el, sel) => el.querySelectorAll(sel);

  function getSliders(container = document) {
    return qsa(container, CART_UPSELL_SLIDER_SELECTOR);
  }

  function getTrack(slider) {
    return qs(slider, TRACK_SELECTOR);
  }

  function getViewport(slider) {
    return qs(slider, '.cart-upsell-slider__viewport');
  }

  function getItemCount(slider) {
    const track = getTrack(slider);
    return track ? track.children.length : 0;
  }

  function isCartPageUpsell(slider) {
    return slider?.dataset?.upsellContext === CART_PAGE_UPSELL_CONTEXT;
  }

  /**
   * Returns sorted array of product IDs from track items (for comparison).
   */
  function getProductIdsFromTrack(slider) {
    const track = getTrack(slider);
    if (!track) return [];
    const ids = [];
    qsa(track, '.cart-upsell-slider__item[data-product-id]').forEach((el) => {
      const id = parseInt(el.dataset.productId, 10);
      if (!isNaN(id)) ids.push(id);
    });
    return ids.sort((a, b) => a - b);
  }

  /**
   * Returns sorted array of product IDs from recommendations HTML string.
   */
  function getProductIdsFromHtml(htmlString) {
    if (!htmlString || !htmlString.trim()) return [];
    try {
      const doc = new DOMParser().parseFromString(htmlString, 'text/html');
      const ids = [];
      qsa(doc, '.cart-upsell-slider__item[data-product-id]').forEach((el) => {
        const id = parseInt(el.dataset.productId, 10);
        if (!isNaN(id)) ids.push(id);
      });
      return ids.sort((a, b) => a - b);
    } catch (_) {
      return [];
    }
  }

  function arraysEqual(a, b) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  /* Layout */

  function setArrowState(btn, visible) {
    if (!btn) return;
    btn.style.display = visible ? 'flex' : 'none';
    btn.setAttribute('tabindex', visible ? '0' : '-1');
    btn.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  function getCarouselColumnCount(slider) {
    const viewport = getViewport(slider);
    if (!viewport) return CAROUSEL_MAX_COL_MOBILE;
    const count = getItemCount(slider);
    if (count <= 1) return CAROUSEL_MAX_COL_MOBILE;

    const w = viewport.clientWidth;
    let maxCols = CAROUSEL_MAX_COL_DESKTOP;
    if (w < CAROUSEL_BREAKPOINT_TABLET) maxCols = CAROUSEL_MAX_COL_MOBILE;
    else if (w < CAROUSEL_BREAKPOINT_DESKTOP) maxCols = CAROUSEL_MAX_COL_TABLET;

    return Math.min(maxCols, count);
  }

  function applyCarouselColumns(slider) {
    const viewport = getViewport(slider);
    if (!viewport) return;
    viewport.style.setProperty('--carousel-cols', String(getCarouselColumnCount(slider)));
  }

  function applyLayoutState(slider) {
    const count = getItemCount(slider);

    slider.classList.remove(LAYOUT_SINGLE, LAYOUT_DOUBLE, LAYOUT_MULTIPLE, LAYOUT_CAROUSEL);

    const prevBtn = qs(slider, '[data-slider-prev]');
    const nextBtn = qs(slider, '[data-slider-next]');
    const viewport = getViewport(slider);

    if (count <= 1) {
      slider.classList.add(LAYOUT_SINGLE);
      viewport?.style.removeProperty('--carousel-cols');
      setArrowState(prevBtn, false);
      setArrowState(nextBtn, false);
      prevBtn?.classList.remove(ARROW_HIDDEN_CLASS);
      nextBtn?.classList.remove(ARROW_HIDDEN_CLASS);
      return;
    }

    if (isCartPageUpsell(slider)) {
      slider.classList.add(LAYOUT_CAROUSEL);
      applyCarouselColumns(slider);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!slider.isConnected) return;
          updateArrowVisibility(slider);
        });
      });
      return;
    }

    /* Cart drawer: original single / double / multi slider */
    viewport?.style.removeProperty('--carousel-cols');
    if (count === 2) {
      slider.classList.add(LAYOUT_DOUBLE);
    } else {
      slider.classList.add(LAYOUT_MULTIPLE);
    }

    const showArrows = count > 2;
    setArrowState(prevBtn, showArrows);
    setArrowState(nextBtn, showArrows);

    if (showArrows) {
      updateArrowVisibility(slider);
      syncArrowVisibilityAfterLayout(slider);
    }
  }

  function updateArrowVisibility(slider) {
    const viewport = getViewport(slider);
    const prevBtn = qs(slider, '[data-slider-prev]');
    const nextBtn = qs(slider, '[data-slider-next]');
    if (!viewport || !prevBtn || !nextBtn) return;

    if (slider.classList.contains(LAYOUT_CAROUSEL)) {
      const { scrollLeft, scrollWidth, clientWidth } = viewport;
      if (clientWidth === 0) return;

      const scrollable = scrollWidth > clientWidth + 2;
      if (!scrollable) {
        setArrowState(prevBtn, false);
        setArrowState(nextBtn, false);
        prevBtn.classList.remove(ARROW_HIDDEN_CLASS);
        nextBtn.classList.remove(ARROW_HIDDEN_CLASS);
        return;
      }

      setArrowState(prevBtn, true);
      setArrowState(nextBtn, true);

      const atLeft = scrollLeft <= 0;
      const atRight = scrollLeft + clientWidth >= scrollWidth - 1;

      prevBtn.classList.toggle(ARROW_HIDDEN_CLASS, atLeft);
      nextBtn.classList.toggle(ARROW_HIDDEN_CLASS, atRight);

      prevBtn.setAttribute('tabindex', atLeft ? '-1' : '0');
      nextBtn.setAttribute('tabindex', atRight ? '-1' : '0');

      prevBtn.setAttribute('aria-hidden', atLeft ? 'true' : 'false');
      nextBtn.setAttribute('aria-hidden', atRight ? 'true' : 'false');
      return;
    }

    if (!slider.classList.contains(LAYOUT_MULTIPLE)) return;

    const { scrollLeft, scrollWidth, clientWidth } = viewport;
    if (clientWidth === 0) return;

    const atLeft = scrollLeft <= 0;
    const atRight = scrollLeft + clientWidth >= scrollWidth - 1;

    prevBtn.classList.toggle(ARROW_HIDDEN_CLASS, atLeft);
    nextBtn.classList.toggle(ARROW_HIDDEN_CLASS, atRight);

    prevBtn.setAttribute('tabindex', atLeft ? '-1' : '0');
    nextBtn.setAttribute('tabindex', atRight ? '-1' : '0');

    prevBtn.setAttribute('aria-hidden', atLeft ? 'true' : 'false');
    nextBtn.setAttribute('aria-hidden', atRight ? 'true' : 'false');
  }

  /**
   * Schedules arrow visibility update after layout/paint so scrollWidth/clientWidth are accurate. 
   * Call after content render, recommendation refresh, or when the upsell details block becomes visible. Idempotent; no extra listeners.
   */
  function syncArrowVisibilityAfterLayout(slider) {
    if (!slider || (!slider.classList.contains(LAYOUT_CAROUSEL) && !slider.classList.contains(LAYOUT_MULTIPLE)))
      return;

    const details = slider.closest(UPSELL_DETAILS_SELECTOR);
    if (details && details.classList.contains('hidden')) return;

    const viewport = getViewport(slider);
    if (!viewport || !viewport.isConnected) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!viewport.isConnected) return;
        const { scrollWidth, clientWidth } = viewport;
        if (clientWidth === 0) return;
        updateArrowVisibility(slider);
      });
    });
  }

  /* Visibility: .cart-upsell-details — hide when zero rendered items */

  /**
   * Counts actual rendered upsell items in the slider track.
   * Used for visibility; does not rely on theme settings.
   */
  function getRenderedUpsellCount(slider) {
    if (!slider) return 0;
    const track = getTrack(slider);
    return track ? qsa(track, '.cart-upsell-slider__item').length : 0;
  }

  /**
   * Unified visibility controller for each upsell details block (drawer + cart page).
   * Hides section when there are zero upsell items to display (manual or dynamic).
   * Runs on init, after cart updates, after recommendation refresh, after in-cart removal.
   * Idempotent; safe to call multiple times.
   */
  function updateUpsellVisibility() {
    qsa(document, UPSELL_DETAILS_SELECTOR).forEach((details) => {
      const slider = qs(details, CART_UPSELL_SLIDER_SELECTOR);
      const itemCount = getRenderedUpsellCount(slider);
      const shouldHide = itemCount === 0;

      details.classList.toggle('hidden', shouldHide);
      details.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');

      if (!shouldHide && slider) syncArrowVisibilityAfterLayout(slider);
    });
  }


  function removeInCartItems(slider, cartProductIds) {
    if (!cartProductIds?.length) return;

    const track = getTrack(slider);
    if (!track) return;

    const idSet = new Set(cartProductIds);

    qsa(track, '.cart-upsell-slider__item[data-product-id]').forEach((item) => {
      const id = parseInt(item.dataset.productId, 10);
      if (!idSet.has(id)) return;

      if (item.contains(document.activeElement)) {
        const fallback = item.nextElementSibling || item.previousElementSibling;
        fallback?.focus?.();
      }

      item.style.transition = 'opacity 0.2s ease';
      item.style.opacity = '0';

      setTimeout(() => {
        item.remove();
        applyLayoutState(slider);
        updateUpsellVisibility();
        syncArrowVisibilityAfterLayout(slider);
      }, 200);
    });
  }

  /* Slider Init */

  function initSlider(slider) {
    if (slider.dataset.upsellInited === 'true') {
      applyLayoutState(slider);
      return;
    }

    const track = getTrack(slider);
    const viewport = getViewport(slider);
    if (!track || !viewport) return;

    if (!initialTrackHtmlBySlider.has(slider)) {
      initialTrackHtmlBySlider.set(slider, track.innerHTML);
    }

    slider.dataset.upsellInited = 'true';

    const prevBtn = qs(slider, '[data-slider-prev]');
    const nextBtn = qs(slider, '[data-slider-next]');

    const getStep = () => {
      const firstItem = qs(track, '.cart-upsell-slider__item');
      const cardWidth = firstItem?.offsetWidth || 220;
      const gap = parseFloat(getComputedStyle(track).gap || '0') || CAROUSEL_GAP_PX;
      return cardWidth + gap;
    };

    const scroll = (dir) =>
      viewport.scrollBy({ left: getStep() * dir, behavior: 'smooth' });

    prevBtn?.addEventListener('click', () => scroll(-1));
    nextBtn?.addEventListener('click', () => scroll(1));

    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scroll(1);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        scroll(-1);
      }
    });

    let scrollTimer;
    viewport.addEventListener(
      'scroll',
      () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => updateArrowVisibility(slider), SCROLL_DEBOUNCE_MS);
      },
      { passive: true }
    );

    const reflowCarousel = () => {
      if (!slider.classList.contains(LAYOUT_CAROUSEL)) return;
      applyCarouselColumns(slider);
      requestAnimationFrame(() => updateArrowVisibility(slider));
    };
    const ro = new ResizeObserver(reflowCarousel);
    ro.observe(viewport);

    applyLayoutState(slider);
  }

  /* Recommendations from Shopify */

  /**
   * Fetches one Search & Discovery intent and returns its rendered upsell items.
   * A failing intent resolves to [] so it never blocks the other one.
   */
  function fetchRecommendationItems(productId, baseUrl, limit, intent, signal) {
    const url = `${baseUrl}?product_id=${productId}&limit=${limit}&section_id=${RECOMMENDATIONS_SECTION_ID}&intent=${intent}`;

    return fetch(url, { signal })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.from(qsa(doc, '#cart-drawer-recommendations-content > .cart-upsell-slider__item'));
      })
      .catch(() => []);
  }

  /**
   * Merges the intents into one track — related first, complementary as top-up — deduped by
   * product id and capped at limit. Tags quick-add modals so stale ones can be reaped later.
   */
  function buildRecommendationsHtml(itemGroups, limit) {
    const seen = new Set();
    const parts = [];

    itemGroups.flat().forEach((item) => {
      if (parts.length >= limit) return;

      const id = item.dataset.productId;
      if (!id || seen.has(id)) return;
      seen.add(id);

      qsa(item, 'quick-add-modal').forEach((modal) => modal.setAttribute(UPSELL_MODAL_FLAG, ''));
      parts.push(item.outerHTML);
    });

    return parts.join('');
  }

  /**
   * Quick-add modals move themselves to <body> on connect (ModalDialog.connectedCallback), so
   * replacing the track orphans them. Reap the closed ones to avoid duplicate element ids.
   */
  function removeStaleUpsellModals() {
    qsa(document.body, `quick-add-modal[${UPSELL_MODAL_FLAG}]:not([open])`).forEach((modal) => modal.remove());
  }

  /**
   * Drops requests whose slider the Section Rendering API has already replaced. Connected sliders
   * are left alone — their in-flight refresh is still the current one.
   */
  function abortDetachedRecommendations() {
    inFlightRecommendations.forEach((entry) => {
      if (entry.slider.isConnected) return;
      entry.controller.abort();
      inFlightRecommendations.delete(entry);
    });
  }

  function loadDynamicRecommendations(slider) {
    const { firstProductId, recommendationsUrl, recommendationsLimit } = slider.dataset;
    if (!firstProductId) return Promise.resolve(null);

    const cached = recommendationsCache[firstProductId];
    if (cached && Date.now() - cached.at < CACHE_MAX_AGE_MS)
      return Promise.resolve(cached.html);

    const base =
      recommendationsUrl?.replace('.json', '') ||
      (window.Shopify?.routes?.root || '') + 'recommendations/products';

    const limit = Math.min(
      Math.max(parseInt(recommendationsLimit, 10) || RECOMMENDATIONS_MAX_LIMIT, 1),
      RECOMMENDATIONS_MAX_LIMIT
    );

    const entry = { controller: new AbortController(), slider };
    inFlightRecommendations.add(entry);

    return Promise.all(
      RECOMMENDATION_INTENTS.map((intent) =>
        fetchRecommendationItems(firstProductId, base, limit, intent, entry.controller.signal)
      )
    )
      .then((groups) => {
        inFlightRecommendations.delete(entry);
        if (entry.controller.signal.aborted) return null;

        const html = buildRecommendationsHtml(groups, limit);
        if (!html) return null;

        recommendationsCache[firstProductId] = { html, at: Date.now() };
        return html;
      })
      .catch(() => {
        inFlightRecommendations.delete(entry);
        return null;
      });
  }

  function applyDynamicRecommendations(slider) {
    if (slider.dataset.dynamicUpsells !== 'true') return;

    const track = getTrack(slider);
    const viewport = getViewport(slider);
    if (!track || !viewport || viewport.dataset.upsellTransitioning === 'true') return;
    if (slider.dataset[REFRESH_DATA_ATTR] === 'true') return;

    slider.dataset[REFRESH_DATA_ATTR] = 'true';

    function clearRefresh() {
      delete slider.dataset[REFRESH_DATA_ATTR];
    }

    loadDynamicRecommendations(slider)
      .then((html) => {
        if (!viewport.isConnected) {
          clearRefresh();
          return;
        }

        const newIds = getProductIdsFromHtml(html || '');
        const currentIds = getProductIdsFromTrack(slider);

        if (html && newIds.length > 0 && arraysEqual(newIds, currentIds)) {
          clearRefresh();
          return;
        }

        if (!html || newIds.length === 0) {
          viewport.dataset.upsellTransitioning = 'true';
          viewport.classList.add(VIEWPORT_TRANSITION_CLASS);

          const onEmptyDone = () => {
            if (!viewport.isConnected) {
              clearRefresh();
              return;
            }
            viewport.classList.remove(VIEWPORT_TRANSITION_CLASS);
            delete viewport.dataset.upsellTransitioning;
            removeStaleUpsellModals();
            track.innerHTML = '';
            lastDynamicRecommendationsHtml = '';
            applyLayoutState(slider);
            updateUpsellVisibility();
            clearRefresh();
          };

          viewport.addEventListener(
            'transitionend',
            (e) => {
              if (e.target === viewport && e.propertyName === 'opacity') onEmptyDone();
            },
            { once: true }
          );
          setTimeout(onEmptyDone, TRANSITION_DURATION_MS + 50);
          return;
        }

        viewport.dataset.upsellTransitioning = 'true';
        removeStaleUpsellModals();
        track.innerHTML = html;
        lastDynamicRecommendationsHtml = html;
        viewport.classList.add(VIEWPORT_TRANSITION_CLASS);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!viewport.isConnected) {
              clearRefresh();
              return;
            }
            viewport.classList.remove(VIEWPORT_TRANSITION_CLASS);
          });
        });

        const onFadeInDone = () => {
          if (!viewport.isConnected) {
            clearRefresh();
            return;
          }
          delete viewport.dataset.upsellTransitioning;
          applyLayoutState(slider);
          updateUpsellVisibility();
          syncArrowVisibilityAfterLayout(slider);
          clearRefresh();
        };

        viewport.addEventListener(
          'transitionend',
          (e) => {
            if (e.target === viewport && e.propertyName === 'opacity') onFadeInDone();
          },
          { once: true }
        );
        setTimeout(onFadeInDone, TRANSITION_DURATION_MS + 50);
      })
      .catch(() => {
        clearRefresh();
      });
  }

  /* Cart Update */

  function getCartProductIds(cartData) {
    return cartData?.items?.map((i) => i.product_id).filter(Boolean) || [];
  }

  function extractCartDataFromPayload(payloadOrEvent) {
    if (!payloadOrEvent) return null;
    if (payloadOrEvent.cartData) return payloadOrEvent.cartData;
    if (payloadOrEvent.detail?.cartData) return payloadOrEvent.detail.cartData;
    return null;
  }

  function initAllSliders(container, cartProductIds) {
    getSliders(container).forEach((slider) => {
      const track = getTrack(slider);
      const isDynamic = slider.dataset.dynamicUpsells === 'true';

      if (isDynamic && lastDynamicRecommendationsHtml && track) {
        removeStaleUpsellModals();
        track.innerHTML = lastDynamicRecommendationsHtml;
        applyLayoutState(slider);
        updateUpsellVisibility();
      }

      if (!isDynamic && track) {
        const initialTrackHtml = initialTrackHtmlBySlider.get(slider);
        if (typeof initialTrackHtml === 'string') {
          track.innerHTML = initialTrackHtml;
        }
      }

      if (cartProductIds?.length) removeInCartItems(slider, cartProductIds);
      initSlider(slider);
      applyDynamicRecommendations(slider);
    });
  }

  function onCartUpdate(payloadOrEvent) {
    abortDetachedRecommendations();
    recommendationsCache = Object.create(null);

    const incoming = extractCartDataFromPayload(payloadOrEvent);
    if (incoming && Array.isArray(incoming.items)) {
      lastKnownCartData = incoming;
    }

    const cartData = incoming || lastKnownCartData;
    const cartProductIds = getCartProductIds(cartData);

    const wrappers = qsa(document, UPSELL_WRAPPER_SELECTOR);
    if (!wrappers.length) return;

    if (addFromUpsellSlider) {
      wrappers.forEach((wrapper) => {
        getSliders(wrapper).forEach((slider) => {
          if (slider.dataset.dynamicUpsells === 'true') {
            const viewport = getViewport(slider);
            viewport?.classList.add(OVERLAY_VISIBLE_CLASS);
          }
        });
      });
      addFromUpsellSlider = false;
    }

    wrappers.forEach((wrapper) => {
      initAllSliders(wrapper, cartProductIds);
    });
    updateUpsellVisibility();
  }

  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const debouncedOnCartUpdate = debounce(onCartUpdate, DEBOUNCE_MS);

  function onResize() {
    qsa(document, UPSELL_WRAPPER_SELECTOR).forEach((wrapper) => {
      getSliders(wrapper).forEach((slider) => {
        if (slider.classList.contains(LAYOUT_CAROUSEL)) {
          applyCarouselColumns(slider);
          syncArrowVisibilityAfterLayout(slider);
        } else if (slider.classList.contains(LAYOUT_MULTIPLE)) {
          syncArrowVisibilityAfterLayout(slider);
        }
      });
    });
  }

  const debouncedOnResize = debounce(onResize, 150);

  function setup() {
    initAllSliders();
    updateUpsellVisibility();

    document.addEventListener(
      'submit',
      (e) => {
        if (
          e.target?.tagName === 'FORM' &&
          e.target.closest(`${UPSELL_WRAPPER_SELECTOR} ${CART_UPSELL_SLIDER_SELECTOR}`)
        ) {
          addFromUpsellSlider = true;
        }
      },
      true
    );

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, debouncedOnCartUpdate);
    }

    document.addEventListener('cart:update', debouncedOnCartUpdate);
    window.addEventListener('resize', debouncedOnResize);
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', setup)
    : setup();
})();